import { useRef, useState, useEffect } from 'react';

import { setErrorList } from 'src/redux/slices/formBuilder';
import { FormElementError } from 'src/@types/forms/enums/FormElementError';
import { PdfOffsetProps, DocumentPageSize, FormViewportProps } from 'src/@types/forms/types';

import useLocales from './useLocales';

//-------------------------------------------

type usePdfHandlingProps = {
  layerSize: DocumentPageSize | null;
  isMobile?: boolean;
  shouldEnableResize?: boolean;
};

const usePdfHandling = ({
  layerSize,
  isMobile,
  shouldEnableResize = true,
}: usePdfHandlingProps) => {
  const { t } = useLocales();
  const [formViewport, setFormViewport] = useState<FormViewportProps[] | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfOffset, setPdfOffset] = useState<PdfOffsetProps>({ xOffset: 0, yOffset: 0 });
  const pdfDocRef = useRef<any>(null);

  const initpageNumberScale = isMobile ? 0.5 : 1;
  const [pageNumberScale, setPageNumberScale] = useState(initpageNumberScale);

  const onDocumentLoadSuccess = async (pdf: any) => {
    const { numPages } = pdf;
    const viewportPages: FormViewportProps[] = [];
    const layerWidth = layerSize && layerSize?.width;

    // Store PDF reference for recalculation on resize
    pdfDocRef.current = pdf;

    // Includes an error that not all pages are viewed
    if (numPages > 1) {
      setErrorList({
        elementID: 'Form',
        errors: [
          {
            elementID: 'Form',
            elementName: 'Form',
            errorCode: FormElementError.NotAllPagesViewed,
            errorMessage: t('forms.validationErrorMessage.NotAllPagesViewed'),
          },
        ],
      });
    }

    setPageNumber(1);
    setNumPages(numPages);

    for (let i = 0; i < numPages; i++) {
      const page = await pdf.getPage(i + 1);

      const viewport = page.getViewport({ scale: 1 });
      const { width, height } = viewport;

      // Extract MediaBox offset from first page
      // page.view returns [x, y, x2, y2] where x,y is the MediaBox origin
      if (i === 0) {
        const pageView = page.view; // [x, y, width, height]
        if (pageView && pageView.length >= 2) {
          setPdfOffset({
            xOffset: pageView[0] || 0,
            yOffset: pageView[1] || 0,
          });
        }

        if (layerWidth) {
          setPageNumberScale(layerWidth / width);
        }
      }

      viewportPages.push({
        pageNumber: i + 1,
        width,
        height,
        scale: layerWidth ? layerWidth / width : 1,
      });
    }

    setFormViewport(viewportPages);
  };

  // Recalculate viewport scales when layerSize changes
  // Extract width to stable primitive value to prevent unnecessary recalculations
  const layerWidth = layerSize?.width;

  useEffect(() => {
    const recalculateViewport = async () => {
      // Check flag inside callback to prevent race conditions
      if (!shouldEnableResize || !pdfDocRef.current || !layerWidth || !numPages) return;

      const viewportPages: FormViewportProps[] = [];

      for (let i = 0; i < numPages; i++) {
        const page = await pdfDocRef.current.getPage(i + 1);
        const viewport = page.getViewport({ scale: 1 });
        const { width, height } = viewport;

        if (i === 0) {
          setPageNumberScale(layerWidth / width);
        }

        viewportPages.push({
          pageNumber: i + 1,
          width,
          height,
          scale: layerWidth / width,
        });
      }

      setFormViewport(viewportPages);
    };

    recalculateViewport();
  }, [layerWidth, numPages, shouldEnableResize]);

  return {
    pageNumber,
    setPageNumber,
    numPages,
    setNumPages,
    onDocumentLoadSuccess,
    formViewport,
    pageNumberScale,
    setPageNumberScale,
    pdfOffset,
  };
};

export default usePdfHandling;
